/* eslint-disable @typescript-eslint/no-misused-promises */
/**
 * Verification code form that shows up post sign up.
 */

import {
  Button,
  Heading,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import TextField from "components/base/atoms/TextField";
import { useGetUserProfileQuery } from "queries";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { setFormErrors } from "utils";
import {
  useConfirmUserQuery,
  useResendCodeQuery,
} from "../../_actions/AuthActions";
import { VerificationCodeSchema } from "../../config/schema/VerificationCodeSchema";
import { ModalTypes } from "../../config/types/modals";
import useAuth from "../../hooks/useAuth";
import { SecondaryButton } from "../base/atoms/HJSButton";
import { SuccessToastHandler } from "../helpers/ToastHelpers";
import { sleep } from "../helpers/miscellaneous";

interface IVerificationCode {
  code: string;
}
interface IModalComponentProps {
  changeModalType: (modalType: ModalTypes) => void;
  onClose: () => void;
}
type TModalComponent = (props: IModalComponentProps) => JSX.Element;

const VerificationCode: TModalComponent = ({
  changeModalType,
  onClose,
}: IModalComponentProps) => {
  const toast = useToast();
  const {
    authState: { email = "" } = {},
    isAuthenticated,
    userDetails,
  } = useAuth();
  const [resendLabel, setResendLabel] = useState(
    "Haven't received the code yet?"
  );
  const [isDisabled, setIsDisabled] = useState(false);
  const initialFormValues: IVerificationCode = {
    code: "",
  };

  const [isLoading, setLoading] = useState(false);
  const { data: userData } = useGetUserProfileQuery();
  const { mutate: confirmUser, isLoading: apiLoading } = useConfirmUserQuery();

  useEffect(() => {
    setLoading(apiLoading);
  }, [apiLoading]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm<IVerificationCode>({
    resolver: yupResolver(VerificationCodeSchema),
    defaultValues: initialFormValues,
  });

  const submitHandler: (values: IVerificationCode) => void = (
    values: IVerificationCode
  ) => {
    const userEmail =
      isAuthenticated && userData?.is_email_verified === false
        ? userDetails?.email
        : email;

    confirmUser(
      {
        email: userEmail,
        code: parseInt(values.code, 10),
      },

      {
        onSuccess: () => {
          SuccessToastHandler({
            toast,
            title: "Success! 🎉",
            description: `Your account has been successfully verified${
              isAuthenticated ? "" : ", please log into your account"
            }.`,
          });
          if (isAuthenticated) {
            onClose();
          } else {
            changeModalType(ModalTypes.LOGIN);
          }
        },

        onError: (error) => {
          setFormErrors(error, setError);
        },
      }
    );
  };

  const { mutate: resendCode } = useResendCodeQuery();

  const resendHandler: () => void = () => {
    setLoading(false);
    const userEmail =
      isAuthenticated && userData?.is_email_verified === false
        ? userDetails?.email
        : email;
    setIsDisabled(true);
    resendCode(
      { email: userEmail },
      {
        onSuccess: async () => {
          setResendLabel("We resent your code, please check your email");
          await sleep(5000);
          setResendLabel("Haven't received the code yet?");
          setIsDisabled(false);
        },
        onError: () => {
          setResendLabel("An error occurred.");
        },
      }
    );
  };

  return (
    <form onSubmit={handleSubmit(submitHandler)}>
      <ModalContent px={{ base: 0, md: 6 }} py={4}>
        <ModalHeader>
          <VStack mb={4} align="flex-start">
            <Heading as="h2" fontSize={[20, 20, 30, 40]} color="teal.900">
              Verify it&apos;s you
            </Heading>
            <Text color="hjsBlack" fontWeight="500" fontSize={[12, 12, 16, 20]}>
              Incorrect Email?
              <Button
                size="xl"
                fontWeight="500"
                color="hjsBlack"
                variant="link"
                onClick={() => {
                  // setUserEmail("");
                  changeModalType(ModalTypes.SIGNUP);
                }}
              >
                &nbsp;Go Back
              </Button>{" "}
            </Text>
          </VStack>
          <Text color="hjsBlack" fontWeight="500" fontSize={[12, 12, 14, 16]}>
            We sent a 6-digit code to your email. Please input that code to
            finish verifying your account.
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack my={6} alignItems="flex-start">
            <TextField
              label="Verification Code"
              placeholder="Enter the 6-digit code"
              error={Boolean(errors.code)}
              helperText={errors.code?.message}
              {...register("code")}
              type="number"
            />
          </VStack>
        </ModalBody>
        <ModalFooter alignItems="center" justifyContent="center">
          <VStack my={4} spacing={4}>
            <SecondaryButton isLoading={isLoading} type="submit">
              Submit Verification Code
            </SecondaryButton>
            <Button
              fontWeight="400"
              color="hjsBlack"
              variant="link"
              textDecor="underline"
              onClick={resendHandler}
              isDisabled={isDisabled}
            >
              {resendLabel}
            </Button>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </form>
  );
};

export default VerificationCode;
